import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Position } from '../types';

interface PositionEditorState {
  editingPosition: string | null;
  editedPositionName: string;
  editedPositionFullName: string;
  showTooltip: string | null;
  coloringPosition: { position: string; depth: number } | null;
}

interface UsePositionEditorProps {
  onSaveCustomPositionLabel: (key: string, name: string, fullName: string) => Promise<boolean>;
  onPositionColorChange: (position: string, depth: number, color: string) => void;
}

interface UsePositionEditorReturn extends PositionEditorState {
  setShowTooltip: (position: string | null) => void;
  setColoringPosition: (pos: { position: string; depth: number } | null) => void;
  setEditedPositionName: (name: string) => void;
  setEditedPositionFullName: (name: string) => void;
  handlePositionEdit: (position: Position) => void;
  savePositionEdit: () => Promise<void>;
  cancelPositionEdit: () => void;
  handlePositionColorChange: (position: string, depth: number, color: string) => void;
}

export const usePositionEditor = ({
  onSaveCustomPositionLabel,
  onPositionColorChange
}: UsePositionEditorProps): UsePositionEditorReturn => {
  const [editingPosition, setEditingPosition] = useState<string | null>(null);
  const [editedPositionName, setEditedPositionName] = useState('');
  const [editedPositionFullName, setEditedPositionFullName] = useState('');
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [coloringPosition, setColoringPosition] = useState<{ position: string; depth: number } | null>(null);

  const handlePositionEdit = useCallback((position: Position) => {
    setEditingPosition(position.key);
    setEditedPositionName(position.name);
    setEditedPositionFullName(position.fullName);
  }, []);

  const savePositionEdit = useCallback(async () => {
    if (!editingPosition || !editedPositionName.trim()) {
      cancelPositionEdit();
      return;
    }

    const success = await onSaveCustomPositionLabel(
      editingPosition, 
      editedPositionName.trim(),
      editedPositionFullName.trim() || editedPositionName.trim()
    );

    if (success) {
      toast.success('Position label saved successfully');
      setEditingPosition(null);
      setEditedPositionName('');
      setEditedPositionFullName('');
    }
  }, [editingPosition, editedPositionName, editedPositionFullName, onSaveCustomPositionLabel]);

  const cancelPositionEdit = useCallback(() => {
    setEditingPosition(null);
    setEditedPositionName('');
    setEditedPositionFullName('');
  }, []);

  const handlePositionColorChange = useCallback((position: string, depth: number, color: string) => {
    onPositionColorChange(position, depth, color);
    setColoringPosition(null);
  }, [onPositionColorChange]);

  return {
    editingPosition,
    editedPositionName,
    editedPositionFullName,
    showTooltip,
    coloringPosition,
    setShowTooltip,
    setColoringPosition,
    setEditedPositionName,
    setEditedPositionFullName,
    handlePositionEdit,
    savePositionEdit,
    cancelPositionEdit,
    handlePositionColorChange
  };
};